import apiClient from './client';

// ── Types ─────────────────────────────────────────────────────────────

export type CollabArtifactType =
  | 'actor'
  | 'campaign'
  | 'indicator'
  | 'rule'
  | 'technique'
  | 'report';

export type CoverageStatus =
  | 'not_started'
  | 'planned'
  | 'in_progress'
  | 'covered'
  | 'at_risk'
  | 'not_applicable';

export interface CollabNote {
  id: number;
  body: string;
  pinned: boolean;
  artifactType: string;
  artifactId: string;
  author: { id: number | null; name: string | null; email: string | null };
  createdAt: string;
  updatedAt: string;
}

export interface CollabCoverage {
  id: number;
  artifactType: string;
  artifactId: string;
  status: CoverageStatus;
  rationale: string | null;
  targetDate: string | null;
  setBy: { id: number | null; name: string | null };
  setAt: string | null;
  cwrFollowedUpAt: string | null;
  updatedAt: string;
}

export interface CollabCoverageHistoryEntry {
  id: number;
  artifactType: string;
  artifactId: string;
  fromStatus: CoverageStatus | null;
  toStatus: CoverageStatus;
  rationale: string | null;
  targetDate: string | null;
  changedBy: { id: number | null; name: string | null };
  changedAt: string;
}

export interface CoverageSummary {
  byStatus: Partial<Record<CoverageStatus, number>>;
  byTypeAndStatus: Record<string, Partial<Record<CoverageStatus, number>>>;
  dueThisWeek: number;
}

export interface CollabPriorIncident {
  id: number;
  artifactType: string;
  artifactId: string;
  /** YYYY-MM-DD — date only, no time component. */
  occurredAt: string;
  summary: string;
  impact: string | null;
  referenceUrl: string | null;
  loggedBy: { id: number | null; name: string | null };
  createdAt: string;
  updatedAt: string;
}

export interface CreatePriorIncidentPayload {
  occurredAt: string;
  summary: string;
  impact?: string | null;
  referenceUrl?: string | null;
}

export interface UpdatePriorIncidentPayload {
  occurredAt?: string;
  summary?: string;
  impact?: string | null;
  referenceUrl?: string | null;
}

// ── Notes ─────────────────────────────────────────────────────────────

export async function fetchNotes(
  artifactType: CollabArtifactType,
  artifactId: string | number,
  page: number = 1,
  limit: number = 50,
): Promise<{ items: CollabNote[]; total: number; page: number; limit: number }> {
  const { data } = await apiClient.get(
    `/collab/artifacts/${artifactType}/${artifactId}/notes`,
    { params: { page, limit } },
  );
  return data;
}

export async function createNote(
  artifactType: CollabArtifactType,
  artifactId: string | number,
  body: string,
  pinned: boolean = false,
): Promise<CollabNote> {
  const { data } = await apiClient.post(
    `/collab/artifacts/${artifactType}/${artifactId}/notes`,
    { body, pinned },
  );
  return data;
}

export async function updateNote(
  id: number,
  changes: { body?: string; pinned?: boolean },
): Promise<CollabNote> {
  const { data } = await apiClient.patch(`/collab/notes/${id}`, changes);
  return data;
}

export async function deleteNote(id: number): Promise<{ id: number }> {
  const { data } = await apiClient.delete(`/collab/notes/${id}`);
  return data;
}

// ── Coverage ──────────────────────────────────────────────────────────

export async function fetchCoverage(
  artifactType: CollabArtifactType,
  artifactId: string | number,
): Promise<{ current: CollabCoverage | null; history: CollabCoverageHistoryEntry[] }> {
  const { data } = await apiClient.get(
    `/collab/artifacts/${artifactType}/${artifactId}/coverage`,
  );
  return data;
}

export async function setCoverage(
  artifactType: CollabArtifactType,
  artifactId: string | number,
  payload: { status: CoverageStatus; rationale?: string | null; targetDate?: string | null },
): Promise<CollabCoverage> {
  const { data } = await apiClient.put(
    `/collab/artifacts/${artifactType}/${artifactId}/coverage`,
    payload,
  );
  return data;
}

export async function fetchCoverageSummary(): Promise<CoverageSummary> {
  const { data } = await apiClient.get('/collab/coverage/summary');
  return data;
}

export interface CollabOverlay {
  noteCounts: Record<string, number>;
  coverage: Record<string, { status: CoverageStatus; targetDate: string | null }>;
  /** Artifact ids that have at least one active prior incident logged. */
  priorIncidents: string[];
}

/**
 * Bulk "overlay" for a list view — one round-trip returns note counts
 * and coverage state keyed by artifact_id. Powers list-row chips and
 * filter bars without N+1 queries.
 */
export async function fetchCollabOverlay(
  artifactType: CollabArtifactType,
): Promise<CollabOverlay> {
  const { data } = await apiClient.get<CollabOverlay>('/collab/overlay', {
    params: { artifactType },
  });
  return data;
}

// ── Prior incidents ──────────────────────────────────────────────────

export async function fetchPriorIncidents(
  artifactType: CollabArtifactType,
  artifactId: string | number,
): Promise<{ items: CollabPriorIncident[] }> {
  const { data } = await apiClient.get(
    `/collab/artifacts/${artifactType}/${artifactId}/prior-incidents`,
  );
  return data;
}

export async function createPriorIncident(
  artifactType: CollabArtifactType,
  artifactId: string | number,
  payload: CreatePriorIncidentPayload,
): Promise<CollabPriorIncident> {
  const { data } = await apiClient.post(
    `/collab/artifacts/${artifactType}/${artifactId}/prior-incidents`,
    payload,
  );
  return data;
}

export async function updatePriorIncident(
  id: number,
  payload: UpdatePriorIncidentPayload,
): Promise<CollabPriorIncident> {
  const { data } = await apiClient.patch(`/collab/prior-incidents/${id}`, payload);
  return data;
}

export async function deletePriorIncident(id: number): Promise<{ id: number }> {
  const { data } = await apiClient.delete(`/collab/prior-incidents/${id}`);
  return data;
}

// ── My notes / recent ────────────────────────────────────────────────

export async function fetchMyNotes(limit: number = 50): Promise<{ items: CollabNote[] }> {
  const { data } = await apiClient.get('/collab/notes/mine', { params: { limit } });
  return data;
}

export async function fetchRecentNotes(limit: number = 50): Promise<{ items: CollabNote[] }> {
  const { data } = await apiClient.get('/collab/notes/recent', { params: { limit } });
  return data;
}

// ── Admin (super_admin only) ─────────────────────────────────────────

export interface AdminCollabNote extends CollabNote {
  orgId: number;
  orgName: string | null;
}

export interface AdminStaleCommitment extends CollabCoverage {
  orgId: number;
  orgName: string | null;
}

export async function fetchAdminRecent(
  days: number = 14,
  limit: number = 100,
): Promise<{ notes: AdminCollabNote[]; events: CollabCoverageHistoryEntry[] }> {
  const { data } = await apiClient.get('/admin/collab/recent', { params: { days, limit } });
  return data;
}

export async function fetchAdminStaleCommitments(
  days: number = 30,
): Promise<{ items: AdminStaleCommitment[] }> {
  const { data } = await apiClient.get('/admin/collab/stale-commitments', { params: { days } });
  return data;
}

export async function markFollowedUp(coverageId: number): Promise<CollabCoverage> {
  const { data } = await apiClient.post(`/admin/collab/${coverageId}/followed-up`);
  return data;
}
